import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Col, DatePicker, Row, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import * as api from '@apis/reservation-success';
import { ReservationSuccessAlertLog } from '@apis/reservation-success';
import { FilterSelect } from '@components/common/FilterSelect';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';
import {
  ALERT_STATUS_COLOR,
  ALERT_STATUS_LABEL,
  RESERVATION_GRADE_COLOR,
  alertStatusLabel,
  alertTypeLabel,
  reservationGradeLabel,
} from '@utils/reservation-success-labels';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_ORDER = ['sent', 'failed', 'skipped', 'pending'] as const;

const ReservationSuccessAlertsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = dayjs().format('YYYY-MM-DD');

  const query = useMemo(
    () => ({
      dateFrom: searchParams.get('dateFrom') || dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
      dateTo: searchParams.get('dateTo') || today,
      status: searchParams.get('status') || undefined,
      alertType: searchParams.get('alertType') || undefined,
      page: Number(searchParams.get('page') || 0),
      size: Number(searchParams.get('size') || 50),
    }),
    [searchParams, today],
  );

  const updateParams = (next: Partial<Record<keyof typeof query, string | number | undefined>>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value === undefined || value === '') params.delete(key);
      else params.set(key, String(value));
    });
    if (!('page' in next)) params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const listQuery = useQuery({
    queryKey: ['reservation-success-alerts', JSON.stringify(query)],
    queryFn: () => api.listReservationSuccessAlertLogs(query),
    refetchInterval: 60000,
  });

  const data = listQuery.data?.data;

  const columns: ColumnsType<ReservationSuccessAlertLog> = [
    {
      title: '알림일',
      dataIndex: 'alertDate',
      width: 110,
    },
    {
      title: '생성 시각',
      dataIndex: 'createdAt',
      width: 130,
      render: (v?: string) => (v ? dayjs(v).format('MM/DD HH:mm:ss') : '-'),
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => <Tag color={ALERT_STATUS_COLOR[v] || 'default'}>{alertStatusLabel(v)}</Tag>,
    },
    {
      title: '종류',
      dataIndex: 'alertType',
      width: 150,
      render: (v: string) => (
        <Tooltip title={v}>
          <span>{alertTypeLabel(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: '파트너',
      dataIndex: 'partnerName',
      width: 140,
      ellipsis: true,
      render: (v: string, row) => (
        <Tooltip title={row.partnerId}>
          <span>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: '등급',
      dataIndex: 'grade',
      width: 90,
      render: (v?: string) => (v ? <Tag color={RESERVATION_GRADE_COLOR[v]}>{reservationGradeLabel(v)}</Tag> : '-'),
    },
    { title: '채널', dataIndex: 'channel', width: 80, render: (v?: string) => v || '-' },
    {
      title: '내용',
      dataIndex: 'message',
      ellipsis: true,
      render: (v?: string, row?: ReservationSuccessAlertLog) => {
        const text = row?.error ? `${v || ''}\n오류: ${row.error}` : v;
        return text ? (
          <Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>}>
            <span>{v || '-'}</span>
            {row?.error && (
              <Text type="danger" style={{ marginLeft: 8 }}>
                {row.error}
              </Text>
            )}
          </Tooltip>
        ) : (
          '-'
        );
      },
    },
    {
      title: '재시도',
      dataIndex: 'retryCount',
      width: 70,
      align: 'right',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <ReservationSuccessPageHeader
        title="파트너 알림 이력"
        description="예약 성사 엔진이 파트너에게 보낸 알림입니다. 같은 회차·종류·날짜의 알림은 한 번만 기록됩니다."
        extra={
          <Button onClick={() => listQuery.refetch()} loading={listQuery.isFetching}>
            새로고침
          </Button>
        }
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker
          value={[dayjs(query.dateFrom), dayjs(query.dateTo)]}
          onChange={(dates) => {
            if (!dates?.[0] || !dates?.[1]) return;
            updateParams({ dateFrom: dates[0].format('YYYY-MM-DD'), dateTo: dates[1].format('YYYY-MM-DD') });
          }}
        />
        <FilterSelect
          inactiveValue=""
          style={{ width: 140 }}
          value={query.status || ''}
          options={[
            { value: '', label: '상태 전체' },
            ...STATUS_ORDER.map((status) => ({ value: status, label: ALERT_STATUS_LABEL[status] })),
          ]}
          onChange={(v) => updateParams({ status: v || undefined })}
        />
        <FilterSelect
          inactiveValue=""
          style={{ width: 200 }}
          value={query.alertType || ''}
          options={[
            { value: '', label: '종류 전체' },
            ...(data?.alertTypes || []).map((row) => ({
              value: row.alertType,
              label: `${alertTypeLabel(row.alertType)} (${row.count})`,
            })),
          ]}
          onChange={(v) => updateParams({ alertType: v || undefined })}
        />
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={4}>
          <Card size="small" hoverable onClick={() => updateParams({ status: undefined })}>
            <Statistic title="전체" value={data?.summary.total ?? 0} loading={listQuery.isLoading} />
          </Card>
        </Col>
        {STATUS_ORDER.map((status) => (
          <Col key={status} xs={12} md={4}>
            <Card
              size="small"
              hoverable
              onClick={() => updateParams({ status })}
              style={query.status === status ? { borderColor: '#1890ff' } : undefined}>
              <Statistic
                title={ALERT_STATUS_LABEL[status]}
                value={data?.summary[status] ?? 0}
                loading={listQuery.isLoading}
                valueStyle={status === 'failed' && data?.summary.failed ? { color: '#fc4242' } : undefined}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Table<ReservationSuccessAlertLog>
        rowKey="_id"
        loading={listQuery.isLoading}
        dataSource={data?.contents || []}
        columns={columns}
        tableLayout="fixed"
        scroll={{ x: 1200 }}
        pagination={{
          current: query.page + 1,
          pageSize: query.size,
          total: data?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100],
        }}
        onChange={(pagination: TablePaginationConfig) =>
          updateParams({ page: (pagination.current || 1) - 1, size: pagination.pageSize || 50 })
        }
      />
    </div>
  );
};

export default ReservationSuccessAlertsPage;
